import { useCallback, useState } from 'react';
import { IGif } from '@giphy/js-types';
import { Compress } from '@frontend/compress';
import { StreamGiphyType } from '@frontend/gif-picker';
import { useTranslation } from '@frontend/i18n';
import { ThreadSendingMediaItem } from '@frontend/integrated-messaging';
import { genUUIDV4 } from '@frontend/string';
import { useAlert } from '@frontend/design-system';
import { MAX_FILE_SIZE } from '../constants';
import { parseGiphyToStreamGifAttachment } from '../utils';

// Create compress instance with same config as Avatar component
const compress = new Compress({
  targetSize: 0.8,
  quality: 0.9,
  maxWidth: 800,
  maxHeight: 600,
});

export type UpdatedThreadSendingMediaItem = ThreadSendingMediaItem & {
  uploadable: boolean;
  type?: string;
  meta?: StreamGiphyType;
};

interface UseImagesUpload {
  hideUploadingImagesEffect: () => void;
  images: UpdatedThreadSendingMediaItem[];
  removeImage: (id: string) => void;
  setImages: (files?: File[]) => Promise<void>;
  showUploadingImagesEffect: () => void;
  addGif: (gif: IGif) => void;
}

type MediaParams = { file: File } | { url: string; meta: StreamGiphyType };

const convertDataToMedia = (input: MediaParams): UpdatedThreadSendingMediaItem => {
  const isInputAFile = 'file' in input;

  return {
    file: isInputAFile ? input.file : new File([], ''),
    hasError: false,
    id: isInputAFile ? genUUIDV4() : input.url,
    mediaObj: {
      filename: isInputAFile ? input.file?.name : '',
      mediaId: isInputAFile ? genUUIDV4() : input.url,
      url: isInputAFile ? URL.createObjectURL(input.file) : input.url,
    },
    previewSrc: isInputAFile ? URL.createObjectURL(input.file) : input.url,
    uploading: false,
    uploadable: isInputAFile,
    meta: !isInputAFile ? input.meta : undefined,
  };
};

export const useImagesUpload = (): UseImagesUpload => {
  const alert = useAlert();
  const { t } = useTranslation('team-chat');

  const [selectedImages, setSelectedImages] = useState<UpdatedThreadSendingMediaItem[]>([]);

  const setImages = useCallback(
    async (files?: File[]) => {
      if (!files?.length) {
        setSelectedImages([]);
        return;
      }

      if (files.length > 10) {
        alert.error(
          t('You can only attach up to 10 images at a time. {{count}} images were ignored.', {
            count: files.length - 10,
          })
        );
      }

      // First filter out files that are too large (before compression)
      const filesToProcess = files.slice(0, 10);
      const validFiles = filesToProcess.filter((file) => file.size <= MAX_FILE_SIZE);
      const removedFiles = filesToProcess.filter((file) => file.size > MAX_FILE_SIZE);

      if (removedFiles.length > 0) {
        alert.error(
          removedFiles.length === 1
            ? t('You can only upload images that are less than 10 MB in size. 1 image was not uploaded.')
            : t('You can only upload images that are less than 10 MB in size. {{count}} images were not uploaded.', {
                count: removedFiles.length,
              })
        );
      }

      try {
        // Compress only the valid files
        const compressedResults = await compress.compress(validFiles);

        // Convert compressed files to uploadable media items
        const uploadableFiles = compressedResults.map((result) => convertDataToMedia({ file: result.photo.data }));

        setSelectedImages([...selectedImages, ...uploadableFiles]);
      } catch (compressionError) {
        console.error('Image compression failed:', compressionError);
        // Fallback to original logic if compression fails
        const uploadableFiles = validFiles.map((file) => convertDataToMedia({ file }));
        setSelectedImages([...selectedImages, ...uploadableFiles]);
      }
    },
    [selectedImages]
  );

  const removeImage = useCallback(
    (id: string) => {
      setSelectedImages(selectedImages.filter((image) => image.id !== id));
    },
    [selectedImages]
  );

  const toggleUploadingEffect = useCallback((uploading: boolean) => {
    setSelectedImages((images = []) => images.map((image) => ({ ...image, uploading })));
  }, []);

  return {
    hideUploadingImagesEffect: () => toggleUploadingEffect(false),
    images: selectedImages,
    removeImage,
    setImages,
    showUploadingImagesEffect: () => toggleUploadingEffect(true),
    addGif: (gif: IGif) => {
      const meta = parseGiphyToStreamGifAttachment(gif);
      setSelectedImages([...selectedImages, convertDataToMedia({ url: meta.thumb_url, meta })]);
    },
  };
};
